import { apiInstance, gql } from "@/services/httpAdapter";
import { errorLogger } from "@Common/helpers/logger";
import { EligibilityFragment, AwardsFragment } from "./fragments";

export async function createSpiffSetup({ locationId, department, commissionCycleId, input }) {
	const query = gql`
		mutation createSpiffSetup($locationId: String!, $department: String!, $commissionCycleId: String!, $input: CreateSetupInput!) {
			createSpiffSetup(locationId: $locationId, department: $department, commissionCycleId: $commissionCycleId, input: $input) {
				_id
				title
				locationId
				department
				effectiveDateFrom
				effectiveDateTo
				itemsCount
				createdAt
				createdBy {
					_id
					fullName
				}
				lastModifiedOn
				lastModifiedBy {
					_id
					fullName
				}
			}
		}
	`;

	const variables = {
		locationId,
		department,
		commissionCycleId,
		input,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response?.data?.data?.createSpiffSetup;
	} catch (err) {
		errorLogger(err, { function: "createSpiffSetup", message: "Error creating spiff setup", variables });
		throw new Error(err);
	}
}

export async function createSpiffItem({ locationId, department, commissionCycleId, input }) {
	const query = gql`
		mutation createSpiffItem($locationId: String!, $department: String!, $commissionCycleId: String!, $input: CreateSpiffItemInput!) {
			createSpiffItem(locationId: $locationId, department: $department, commissionCycleId: $commissionCycleId, input: $input) {
				_id
				locationId,
				department,
				title
				effectiveDateFrom
				effectiveDateTo
				itemsCount
				items {
					_id
					title
					rolloverCounter
					eligibility {
						...EligibilityFields
					}
					effectiveDateRangeFrom
					effectiveDateRangeTo
					reward
					type
					description
					createdAt
					spiffType
					eligibilityDays
					positions
					requiredDealStatus
					transactionType
					awards {
						...AwardsFields
					}
				}
				createdAt
				createdBy {
					_id
					fullName
				}
				lastModifiedOn
				lastModifiedBy {
					_id
					fullName
				}
				commissionCycle {
					_id
					title
					startDate
				}
			}
		}
		${EligibilityFragment}
		${AwardsFragment}
	`;

	const variables = {
		locationId,
		department,
		commissionCycleId,
		input,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response?.data?.data?.createSpiffItem;
	} catch (err) {
		errorLogger(err, { function: "createSpiffItem", message: "Error creating spiff item", variables });
		return null;
	}
}

export async function updateSpiffSetup({ locationId, department, commissionCycleId, spiffId, input }) {
	const query = gql`
		mutation updateSpiffSetup(
			$locationId: String!
			$department: String!
			$commissionCycleId: String!
			$spiffId: String!
			$input: UpdateSpiffSetupInput!
		) {
			updateSpiffSetup(
				locationId: $locationId
				department: $department
				commissionCycleId: $commissionCycleId
				spiffId: $spiffId
				input: $input
			) {
				_id
				title
				locationId
				department
				effectiveDateFrom
				effectiveDateTo
				itemsCount
				items {
					_id
					title
					rolloverCounter
					eligibility {
						...EligibilityFields
					}
					effectiveDateRangeFrom
					effectiveDateRangeTo
					reward
					type
					description
					createdAt
					spiffType
					eligibilityDays
					positions
					requiredDealStatus
					transactionType
					awards {
						...AwardsFields
					}
				}
				createdAt
				createdBy {
					_id
					fullName
				}
				lastModifiedOn
				lastModifiedBy {
					_id
					fullName
				}
			}
		}
		${EligibilityFragment}
		${AwardsFragment}
	`;

	const variables = {
		locationId,
		department,
		commissionCycleId,
		spiffId,
		input,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response?.data?.data?.updateSpiffSetup;
	} catch (err) {
		errorLogger(err, { function: "updateSpiffSetup", message: "Error updating spiff setup", variables });
		return null;
	}
}

export async function updateSpiffSetupTitle({ locationId, department, commissionCycleId, spiffId, title }) {
	const query = gql`
		mutation updateSpiffSetup(
			$locationId: String!
			$department: String!
			$commissionCycleId: String!
			$spiffId: String!
			$input: UpdateSpiffSetupInput!
		) {
			updateSpiffSetup(
				locationId: $locationId
				department: $department
				commissionCycleId: $commissionCycleId
				spiffId: $spiffId
				input: $input
			) {
				_id
				title
				locationId
				department
				effectiveDateFrom
				effectiveDateTo
				itemsCount
				items {
					_id
					title
					rolloverCounter
					eligibility {
						...EligibilityFields
					}
					effectiveDateRangeFrom
					effectiveDateRangeTo
					reward
					type
					description
					createdAt
					spiffType
					eligibilityDays
					positions
					requiredDealStatus
					transactionType
					awards {
						...AwardsFields
					}
				}
				createdAt
				createdBy {
					_id
					fullName
				}
				lastModifiedOn
				lastModifiedBy {
					_id
					fullName
				}
			}
		}
		${EligibilityFragment}
		${AwardsFragment}
	`;

	const variables = {
		locationId,
		department,
		commissionCycleId,
		spiffId,
		input: {
			title,
		},
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response?.data?.data?.updateSpiffSetup;
	} catch (err) {
		errorLogger(err, { function: "updateSpiffSetupTitle", message: "Error updating spiff setup title", variables });
		return null;
	}
}

export async function updateSpiffEffectiveDate({ locationId, department, commissionCycleId, spiffId, effectiveDate, effectiveDateValue }) {
	const query = gql`
		mutation updateSpiffSetup(
			$locationId: String!
			$department: String!
			$commissionCycleId: String!
			$spiffId: String!
			$input: UpdateSpiffSetupInput!
		) {
			updateSpiffSetup(
				locationId: $locationId
				department: $department
				commissionCycleId: $commissionCycleId
				spiffId: $spiffId
				input: $input
			) {
				_id
				title
				locationId
				department
				effectiveDateFrom
				effectiveDateTo
				itemsCount
				items {
					_id
					title
					rolloverCounter
					eligibility {
						...EligibilityFields
					}
					effectiveDateRangeFrom
					effectiveDateRangeTo
					reward
					type
					description
					createdAt
					spiffType
					eligibilityDays
					positions
					requiredDealStatus
					transactionType
					awards {
						...AwardsFields
					}
				}
				createdAt
				createdBy {
					_id
					fullName
				}
				lastModifiedOn
				lastModifiedBy {
					_id
					fullName
				}
			}
		}
		${EligibilityFragment}
		${AwardsFragment}
	`;

	const variables = {
		locationId,
		department,
		commissionCycleId,
		spiffId,
		input: {
			[effectiveDate]: effectiveDateValue,
		},
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateSpiffSetup;
	} catch (err) {
		errorLogger(err, { function: "updateSpiffEffectiveDate", message: "Error updating spiff effective date", variables });
		return null;
	}
}

export async function deleteSpiffSetup({ locationId, department, commissionCycleId, spiffId }) {
	const query = gql`
		mutation deleteSpiffSetup($locationId: String!, $department: String!, $commissionCycleId: String!, $spiffId: String!) {
			deleteSpiffSetup(locationId: $locationId, department: $department, commissionCycleId: $commissionCycleId, spiffId: $spiffId) {
				_id
				locationId
				department
			}
		}
	`;

	const variables = {
		locationId,
		department,
		commissionCycleId,
		spiffId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response?.data?.data?.deleteSpiffSetup;
	} catch (err) {
		errorLogger(err, { function: "deleteSpiffSetup", message: "Error deleting spiff setup", variables });
		return null;
	}
}

export async function deleteSpiffItems({ locationId, department, commissionCycleId, spiffId, itemIds }) {
	const query = gql`
		mutation deleteSpiffItems(
			$locationId: String!
			$department: String!
			$commissionCycleId: String!
			$spiffId: String!
			$itemIds: [String]
		) {
			deleteSpiffItems(
				locationId: $locationId
				department: $department
				commissionCycleId: $commissionCycleId
				spiffId: $spiffId
				itemIds: $itemIds
			) {
				_id
				title
				locationId,
				department,
				effectiveDateFrom
				effectiveDateTo
				itemsCount
				items {
					_id
					title
					rolloverCounter
					eligibility {
						...EligibilityFields
					}
					effectiveDateRangeFrom
					effectiveDateRangeTo
					reward
					type
					description
					spiffType
					eligibilityDays
					positions
					requiredDealStatus
					transactionType
					awards {
						...AwardsFields
					}
				}
				commissionCycle {
					_id
					title
					startDate
				}
				createdAt
				createdBy {
					_id
					fullName
				}
				lastModifiedOn
				lastModifiedBy {
					_id
					fullName
				}
			}
		}
		${EligibilityFragment}
		${AwardsFragment}
	`;

	const variables = {
		locationId,
		department,
		commissionCycleId,
		itemIds,
		spiffId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response?.data?.data?.deleteSpiffItems;
	} catch (err) {
		errorLogger(err, { function: "deleteSpiffItems", message: "Error deleting spiff items", variables });
		return null;
	}
}

export async function updateSpiffItem({ locationId, department, commissionCycleId, spiffSetupId, spiffItemId, input }) {
	const query = gql`
		mutation updateSpiffItem(
			$locationId: String!
			$department: String!
			$spiffSetupId: String!
			$spiffItemId: String!
			$commissionCycleId: String!
			$input: UpdateSpiffItemInput!
		) {
			updateSpiffItem(
				locationId: $locationId
				department: $department
				spiffSetupId: $spiffSetupId
				spiffItemId: $spiffItemId
				commissionCycleId: $commissionCycleId
				input: $input
			) {
				_id
				title
				locationId,
				department,
				effectiveDateFrom
				effectiveDateTo
				itemsCount
				items {
					_id
					title
					rolloverCounter
					eligibility {
						...EligibilityFields
					}
					effectiveDateRangeFrom
					effectiveDateRangeTo
					reward
					type
					description
					createdBy {
						_id
						fullName
					}
					createdAt
					spiffType
					eligibilityDays
					positions
					requiredDealStatus
					transactionType
					awards {
						...AwardsFields
					}
					awardsCount
				}
				createdAt
				createdBy {
					_id
					fullName
				}
				lastModifiedOn
				lastModifiedBy {
					_id
					fullName
				}
				commissionCycle {
					_id
					title
					startDate
				}
			}
		}
		${EligibilityFragment}
		${AwardsFragment}
	`;
	const variables = {
		locationId,
		department,
		commissionCycleId,
		input,
		spiffItemId,
		spiffSetupId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response?.data?.data?.updateSpiffItem;
	} catch (err) {
		errorLogger(err, { function: "updateSpiffItem", message: "Error updating spiff item", variables });
		return null;
	}
}
